import { N8nModule } from '../../types';

export const module4: N8nModule = {
  id: 'module4',
  title: 'Advanced n8n Features',
  description: 'Develop custom nodes, master Function nodes, implement complex conditional logic, and build reusable sub-workflows.',
  duration: '2 hours',
  objectives: [
    'Develop custom nodes in JavaScript',
    'Master Function and Function Item nodes',
    'Implement complex conditional logic',
    'Build reusable sub-workflows',
    'Use advanced flow control patterns',
    'Leverage external libraries in workflows'
  ],
  content: `# Advanced n8n Features

## Custom Node Development

Build custom integrations by creating your own n8n nodes. Perfect for proprietary APIs or specialized workflows.

### Node Structure
- Node properties and parameters
- Input/output configuration
- Credential management
- Execute method implementation

### Publishing Custom Nodes
Share your nodes with the community via npm packages.

## Function Nodes Mastery

Execute custom JavaScript code for data transformation and complex logic.

### Function Node vs Function Item Node
- **Function Node**: Processes all items at once
- **Function Item Node**: Processes one item at a time

### Best Practices
- Keep functions focused and testable
- Use async/await for asynchronous operations
- Leverage lodash and other available libraries
- Handle errors gracefully

## Advanced Flow Control

Master complex routing and iteration patterns.

### IF Node Patterns
- Multiple conditions with AND/OR logic
- Regex pattern matching
- Null/undefined checks

### Switch Node
Route items to different branches based on rules.

### Loop Nodes
Iterate through arrays and paginated APIs.

## Sub-workflows

Create modular, reusable workflow components.

### Benefits
- Code reuse across workflows
- Easier testing and debugging
- Better organization
- Parameterized workflows
`,
  videoUrl: 'https://www.youtube.com/watch?v=example4',
  codeExamples: [
    {
      title: 'Custom Node Template',
      explanation: 'Basic structure for a custom n8n node',
      code: `import { IExecuteFunctions } from 'n8n-workflow';

export class MyCustomNode {
  description = {
    displayName: 'My Custom Node',
    name: 'myCustomNode',
    group: ['transform'],
    version: 1,
    description: 'Custom node description',
    defaults: {
      name: 'My Custom Node',
    },
    inputs: ['main'],
    outputs: ['main'],
    properties: [
      {
        displayName: 'API Key',
        name: 'apiKey',
        type: 'string',
        default: '',
        required: true,
      },
    ],
  };

  async execute(this: IExecuteFunctions) {
    const items = this.getInputData();
    const returnData = [];

    for (let i = 0; i < items.length; i++) {
      const apiKey = this.getNodeParameter('apiKey', i) as string;
      
      // Your custom logic here
      const result = await this.helpers.request({
        method: 'GET',
        url: 'https://api.example.com/data',
        headers: {
          'Authorization': 'Bearer ' + apiKey
        }
      });
      
      returnData.push({ json: result });
    }

    return [returnData];
  }
}`,
      language: 'typescript'
    },
    {
      title: 'Function Node with External API',
      explanation: 'Make HTTP requests in Function node',
      code: `const axios = require('axios');

const items = [];

for (const item of $input.all()) {
  try {
    const response = await axios.get('https://api.example.com/data', {
      headers: {
        'Authorization': 'Bearer ' + item.json.apiToken
      },
      params: {
        userId: item.json.userId
      }
    });
    
    items.push({
      json: {
        userId: item.json.userId,
        data: response.data,
        fetchedAt: new Date().toISOString()
      }
    });
  } catch (error) {
    items.push({
      json: {
        userId: item.json.userId,
        error: error.message
      }
    });
  }
}

return items;`,
      language: 'javascript'
    }
  ],
  practiceExercise: {
    title: 'Build a Custom n8n Integration',
    description: 'Create a custom node or Function-based workflow that integrates with a specific API of your choice.',
    requirements: [
      'Handle authentication securely',
      'Implement error handling',
      'Support pagination if applicable',
      'Include comprehensive logging'
    ],
    hints: [
      'Start with the Function node before creating a custom node',
      'Test with small datasets first',
      'Use n8n expression syntax for dynamic values'
    ]
  },
  quiz: {
    questions: [
      {
        id: 'q1-function-nodes',
        question: 'What is the main difference between Function and Function Item nodes?',
      options: [
        'Function Item is faster',
        'Function processes all items, Function Item processes one at a time',
        'Function Item cannot use async/await',
        'There is no difference'
      ],
      correctAnswer: 1,
      explanation: 'Function node processes all items together, while Function Item processes items one by one, which is useful for item-specific transformations.'
      },
      {
        id: 'q2-custom-nodes-language',
        question: 'Which programming language is used to develop custom n8n nodes?',
      options: ['Python', 'JavaScript/TypeScript', 'Java', 'Go'],
      correctAnswer: 1,
      explanation: 'Custom n8n nodes are developed using JavaScript or TypeScript, following the n8n node development framework.'
      }
    ]
  }
};
